/**
 * useObjectives Hook
 *
 * Centraliza la carga + mutaciones del listado de campañas para una marca.
 * Las páginas y componentes consumen este hook en lugar de llamar la API
 * directamente.
 */

import { useCallback, useEffect, useState } from "react";
import { objectivesApi } from "../api";
import type {
  Objective,
  CreateObjectiveDTO,
  UpdateObjectiveDTO,
  ObjectivePreset,
} from "../types";

export function useObjectives(brandId: string) {
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  /* ─── Load ─── */
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await objectivesApi.list(brandId);
    if (res.success) {
      setObjectives(res.data);
    } else {
      setError(res.error || "Error al cargar campañas");
    }
    setLoading(false);
  }, [brandId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /* ─── Create draft ─── */
  const createDraft = useCallback(
    async (dto: Omit<CreateObjectiveDTO, "brandId">) => {
      setError(null);
      const res = await objectivesApi.create({ brandId, ...dto });
      if (res.success) {
        await refresh();
        return res.data;
      }
      setError(res.error || "Error al crear la campaña");
      return null;
    },
    [brandId, refresh],
  );

  /* ─── Update ─── */
  const update = useCallback(
    async (id: string, dto: UpdateObjectiveDTO) => {
      setBusyId(id);
      setError(null);
      const res = await objectivesApi.update(id, dto);
      setBusyId(null);
      if (res.success) {
        await refresh();
        return res.data;
      }
      setError(res.error || "Error al actualizar la campaña");
      return null;
    },
    [refresh],
  );

  /* ─── Activate / Pause ─── */
  const activate = useCallback(
    async (id: string) => {
      setBusyId(id);
      setError(null);
      const res = await objectivesApi.activate(id);
      setBusyId(null);
      if (res.success) {
        await refresh();
        return true;
      }
      setError(res.error || "Error al activar");
      return false;
    },
    [refresh],
  );

  const pause = useCallback(
    async (id: string) => {
      setBusyId(id);
      setError(null);
      const res = await objectivesApi.pause(id);
      setBusyId(null);
      if (res.success) {
        await refresh();
        return true;
      }
      setError(res.error || "Error al pausar");
      return false;
    },
    [refresh],
  );

  /* ─── Duplicate ─── */
  const duplicate = useCallback(
    async (id: string) => {
      setBusyId(id);
      setError(null);
      const res = await objectivesApi.duplicate(id);
      setBusyId(null);
      if (res.success) {
        await refresh();
        return res.data;
      }
      setError(res.error || "Error al duplicar");
      return null;
    },
    [refresh],
  );

  /* ─── Delete ─── */
  const remove = useCallback(
    async (id: string) => {
      setBusyId(id);
      setError(null);
      const res = await objectivesApi.delete(id);
      setBusyId(null);
      if (res.success) {
        await refresh();
        return true;
      }
      setError(res.error || "Error al borrar");
      return false;
    },
    [refresh],
  );

  /* ─── AI suggest (Auto mode) ─── */
  const suggest = useCallback(async (preset: ObjectivePreset) => {
    setError(null);
    const res = await objectivesApi.suggest(preset);
    if (res.success) return res.data;
    setError(res.error || "Error al pedir sugerencia");
    return null;
  }, []);

  return {
    objectives,
    loading,
    error,
    busyId,
    refresh,
    createDraft,
    update,
    activate,
    pause,
    duplicate,
    remove,
    suggest,
  };
}
